import Bowser from 'bowser';
import store from '@Store';
import {
    NOTIFICATION_HANDLER,
    DOCUMENT_MARKUP,
    BASE_LAYOUT_RERENDER,
    NAVIGATION
} from '@Constants/types';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import * as commonUtils from '@Utils/commonUtils';
import { ScorecardStatus } from '@Constants/status';

export default function GetWordVersion() {
    /* Example values for the hostInfoValue token
        Word|Win32|16.00|en-US									                    Installed Word PC
        Word$Mac$16.01$en-US$$$$0 Mac								                Installed Word MAC
        Word|Web|16.00|EN-GB|87bdaede-7597-414f-a50b-b754cc464951|||0				Word Online PC
        Word|Web|16.00|EN-US|5f4ae69a-0d6b-4a0f-9e4e-deebba9f7924|||0 Word Online	Online MAC
    */
    let isWordOnline = false;
    let majVersion = 16;
    let minVersion = 0;
    let versionParts = null;
    const vers = window.sessionStorage.hostInfoValue;
    if (vers && vers.length > 0) {
        if (vers.includes('$')) {
            versionParts = vers.split('$');
        } else if (vers.includes('|')) {
            versionParts = vers.split('|');
        }
    }

    let hostPlatform = '';
    if (versionParts && versionParts.length > 2) {
        const [, platform, versionNumber] = versionParts;

        if (platform === 'Web') {
            isWordOnline = true;
        }

        hostPlatform = platform;

        if (versionNumber.length > 0 && versionNumber.includes('.')) {
            const versionNumberParts = versionNumber.split('.');
            if (versionNumberParts.length > 0) {
                const [majorVersion, minorVersion] = versionNumberParts;
                majVersion = Number(majorVersion);
                minVersion = Number(minorVersion);
            }
        }
    }

    const retValue = {
        majorVersion: majVersion,
        minorVersion: minVersion,
        isWordOnline,
        platform: hostPlatform
    };

    return retValue;
}

export function isLegacyEdgeBrowser() {
    // This method returns true for BOTH old Edge and new Edge browser
    if (navigator.userAgent.indexOf('Mozilla') > -1 && navigator.userAgent.indexOf('Edge') > -1) {
        return true;
    }

    return false;
}

export function isEdgeBrowser() {
    // This method returns true for BOTH old Edge and new Edge browser
    if (navigator.userAgent.indexOf('Mozilla') > -1 && navigator.userAgent.indexOf('Edg') > -1) {
        return true;
    }

    return false;
}

export function isEdgiumBrowser() {
    // Thie method returns true ONLY for the new Edge browser
    if (navigator.userAgent.indexOf('Mozilla') > -1 && navigator.userAgent.indexOf('Edg/') > -1) {
        return true;
    }

    return false;
}

export function isMacintosh() {
    if (navigator.userAgent.indexOf('Mac') > -1) {
        return true;
    }

    return false;
}

export function isIE11Browser() {
    if (
        navigator.userAgent.indexOf('Mozilla') > -1 &&
        navigator.userAgent.indexOf('rv:11.0') > -1
    ) {
        return true;
    }

    return false;
}

export function isWordOnlineWithIE11() {
    const isIE11 = isIE11Browser();
    const version = GetWordVersion();

    if (version.isWordOnline && isIE11) {
        return true;
    }

    return false;
}

export function isWordonline() {
    const version = GetWordVersion();

    if (version.isWordOnline) {
        return true;
    }

    return false;
}

export function isMSWordForWindows() {
    const version = GetWordVersion();

    if (version.majorVersion === 16 && !version.isWordOnline && version.platform === 'Win32') {
        return true;
    }

    return false;
}

export function isMSWordWithEdge() {
    const isEdge = isEdgeBrowser();
    const version = GetWordVersion();

    if (
        version.majorVersion === 16 &&
        !version.isWordOnline &&
        version.platform === 'Win32' &&
        isEdge
    ) {
        return true;
    }

    return false;
}

export function getPerformanceFactors() {
    const CPUs = navigator.hardwareConcurrency;
    const isWOL = isWordonline();
    const isIE11 = isIE11Browser();
    return { CPUs, isWOL, isIE11 };
}

export function getSkipCount() {
    let skip = 0;
    const rootReducer = store.getState();
    const { document } = rootReducer;
    const { searchCount } = document;

    if (searchCount >= 10 && searchCount < 20) skip = 1;
    if (searchCount >= 20 && searchCount < 30) skip = 2;
    if (searchCount >= 30 && searchCount < 40) skip = 3;
    if (searchCount >= 40 && searchCount < 50) skip = 4;
    if (searchCount >= 50) skip = 5;

    if (searchCount > 0) {
        store.dispatch({
            type: DOCUMENT_MARKUP.SEARCH_COUNT,
            payload: 0
        });
    }

    return skip;
}

export function getMarkupFrequency() {
    // const perf = getPerformanceFactors();
    let batchSize = 20;
    let delay = 1100;

    /*
    switch (perf.CPUs) {
        case 1:
            batchSize = 5;
            delay = 1400;
            break;
        case 2:
            batchSize = 10;
            delay = 1200;
            break;
        case 4:
            batchSize = 15;
            delay = 1000;
            break;
        case 8:
            delay = 800;
            batchSize = 20;
            break;
        case 12:
            delay = 600;
            batchSize = 20;
            break;
        case 16:
            delay = 400;
            batchSize = 25;
            break;
        default:
            break;
    }

    if (perf.isWOL && !perf.isIE11) {
        batchSize += 5; // Word Online is significantly more performant than MSWord
        delay = delay * 0.8; // Word Online is significantly more performant than MSWord
    }
    */

    return { delay, batchSize };
}

export function getBasename() {
    const regex = /\/v(\d+)\.(\d+)/g;
    const basename = window.location.pathname.match(regex);
    return !!basename ? basename[0] : '';
}

export function getCurrentPage() {
    const pageName = window.location.pathname;

    return pageName.substring(1, pageName.length);
}

export function defaultDigiApp(digApps) {
    try {
        const digiApps = Object.values(digApps);

        if (!digiApps || digiApps.length === 0) {
            return '';
        }

        let defaultApp = '';
        const [firstApp] = digiApps;
        if (firstApp) {
            const { text: name } = firstApp;
            if (digiApps.length === 1) {
                // if only one digiApp select it
                defaultApp = name;
            } else if (digiApps.length > 1) {
                let containsULEX = false;
                let containNDA = false;
                for (let i = 0; i < digiApps.length; i += 1) {
                    if (digiApps[i].text === 'ULex') containsULEX = true;
                    if (digiApps[i].text === 'NDA') containNDA = true;
                }

                if (containsULEX) {
                    // preference is to default to ULex (if it exists)
                    defaultApp = 'ULex';
                } else if (containNDA) {
                    // second preference is to default to NDA (if it exists)
                    defaultApp = 'NDA';
                } else {
                    // otherwise randomly choose the first one in the list
                    defaultApp = name;
                }
            }
        }
        return defaultApp;
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'defaultDigiApp', err);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });

        return '';
    }
}

export function isEmptyObj(obj) {
    if (Object.keys(obj).length === 0) {
        return true;
    }
    return false;
}

export function removeTrailingSlash(url) {
    /**
     * return the valid part of the url if not return the url written
     */
    const trimmedUrl = url.match(/\b(https:\/\/[\w-]+\.[\w-]+\.[\w-]+)\b/g);
    return trimmedUrl ? trimmedUrl[0] : url;
}
export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

export function whichBrowser() {
    const browser = Bowser.getParser(window.navigator.userAgent);
    let browserName = browser.getBrowserName();
    if (browserName === 'Microsoft Edge' && navigator.userAgent.indexOf('Edg/') > -1) {
        browserName = 'Edge Chromium'; // Distinguish between old and new Edge
    }

    return browserName;
}

// Generate a unique GUID
export function guid() {
    let sGuid = '';
    for (let i = 0; i < 32; i += 1) {
        let numb = Math.random() * 15;
        numb = Math.floor(numb);
        const sNumb = numb.toString(0xf);
        sGuid = `${sGuid}${sNumb}`;
    }

    return sGuid;
}

export function calculateOverflow(page) {
    try {
        const rootReducer = store.getState();
        if (page !== '') {
            // store the current page in the reducer
            store.dispatch({ type: NAVIGATION.CURRENT_TAB, payload: page });
        } else {
            // use last page
            const { headerNavigation } = rootReducer;
            const { currentTab } = headerNavigation;
            page = currentTab;
        }

        addLog(LOG_TYPE.INFO, 'calculateOverflow', 'Start');
        addLog(LOG_TYPE.INFO, 'calculateOverflow', `page = ${page}`);

        const { document, base, scoreCard, digAppsSelector } = rootReducer;
        const { metadata, isSubmitting, documentStatus } = document;
        const { scorecardStatus } = scoreCard;
        const { digAppsSelectionInput } = digAppsSelector;
        const { overflowy: existingOverflowy } = base;

        let documentProcessed = metadata && metadata.items && metadata.items.length > 0;

        if (documentProcessed === undefined) documentProcessed = false;
        addLog(LOG_TYPE.INFO, 'calculateOverflow', `documentProcessed = ${documentProcessed}`);
        addLog(LOG_TYPE.INFO, 'calculateOverflow', `isSubmitting = ${isSubmitting}`);
        addLog(LOG_TYPE.INFO, 'calculateOverflow', `documentStatus = ${documentStatus}`);
        addLog(LOG_TYPE.INFO, 'calculateOverflow', `scorecardStatus = ${scorecardStatus}`);

        addLog(LOG_TYPE.INFO, 'calculateOverflow', `existingOverflowy = ${existingOverflowy}`);

        let overflowy = 'auto'; // for all non-IE11 browsers default to auto
        const isIE11 = commonUtils.isIE11Browser();
        if (isIE11) {
            overflowy = 'scroll'; // for IE11, default to scroll
            if (page === 'scorecards' || page === 'labels') {
                overflowy = documentProcessed ? 'scroll' : 'visible';
            }
        }

        // if the user has just clicked on the submit document button then we can have a static scrollbar-less overflow
        if (page === 'submitDocument') {
            overflowy = 'visible';
            store.dispatch({ type: NAVIGATION.CURRENT_TAB, payload: 'scorecards' });
        }

        // if we are still submitting then we can have a static scrollbar-less overflow
        if (isSubmitting) {
            overflowy = 'visible';
        }

        // dont perform this additional check if there are no scorecards enabled
        if (Object.keys(digAppsSelectionInput).length > 0) {
            // if the scorecard is still being analyzed then we can have a static scrollbar-less overflow
            if (
                (page === 'scorecards' || page === 'labels') &&
                scorecardStatus === ScorecardStatus.NOT_ANALYZED &&
                isSubmitting !== undefined
            ) {
                overflowy = 'visible';
            }
        }

        if (existingOverflowy !== overflowy) {
            addLog(LOG_TYPE.INFO, 'calculateOverflow', `change overflowy = ${overflowy}`);
            store.dispatch({ type: BASE_LAYOUT_RERENDER, payload: overflowy });
        }
    } catch (err) {
        addLog(LOG_TYPE.ERROR, 'calculateOverflow', `err = ${err}`);
        store.dispatch({
            type: NOTIFICATION_HANDLER.ERROR,
            payload: err
        });
    }
}

/* eslint-disable prettier/prettier */
export function decodeHTMLEntities(text) {
    var entities = [
        ['amp', '&'],
        ['apos', "'"],
        ['#x27', "'"],
        ['#x2F', '/'],
        ['#39', "'"],
        ['#47', '/'],
        ['lt', '<'],
        ['gt', '>'],
        ['nbsp', ' '],
        ['quot', '"']
    ];

    for (var i = 0, max = entities.length; i < max; ++i)
        text = text.replace(new RegExp('&' + entities[i][0] + ';', 'g'), entities[i][1]);

    return text;
}

export function decodeHtmlEntity(x) {
    return x.replace(/&#(\d+);/g, function(match, dec) {
        return String.fromCharCode(dec);
    });
}

export function decodeHtmlText(text) {
    if (text === undefined) return text;
    return decodeHtmlEntity(decodeHTMLEntities(text));
}
